/**
 * An open page picks up a new build when the person comes back to it (REQ-2026-09-25-10).
 *
 * Mike, 2026-09-25, of the version selector deployed that morning: "I had to refresh the page to se this". The
 * page he had open was the build from before the deploy: the portal is one bundle loaded once, so nothing a deploy
 * changes reaches a page until it is reloaded, and every deploy since the first has been invisible that way to
 * anyone who leaves Cobrain open, which is how it is meant to be used.
 *
 * THE RULE (Mike's choice by pop-up, 2026-09-25: "Show an 'updated' line", the option reading "A line at the top
 * says Cobrain has been updated, with a Reload button. Nothing reloads until you press it."). When the person
 * comes back to the window, and every five minutes while they look at it, ask the server for index.html, which it
 * serves no-store, and compare its entry script with the one this page was loaded from. The asset name carries a
 * content hash, so a different name is a different build and the same name is the same build. On a new build the
 * page says so in a line under the bar with a Reload button, and nothing reloads until it is pressed: a reload
 * the page chose would throw away a draft half written. Rejected by him: reloading on return; leaving it.
 */
import { viewFetch as fetch } from './view-as.js';
import { useState } from 'react';
import { useLiveRefresh } from './live.js';

/** index.html is a few hundred bytes, served no-store: a look every five minutes costs nothing. */
const LOOK_MS = 5 * 60_000;

/** The entry script an index.html loads, e.g. `/assets/index--roblL2k.js`; null when it names none. */
export function entryScript(html: string): string | null {
  const m = /<script\b[^>]*\btype=["']module["'][^>]*\bsrc=["']([^"']+)["']/i.exec(html)
    ?? /<script\b[^>]*\bsrc=["']([^"']+)["'][^>]*\btype=["']module["']/i.exec(html);
  return m ? m[1]! : null;
}

/** Only a path under /assets/ is a built bundle; the dev server's /src/main.tsx never counts as a new build. */
export function isNewBuild(served: string | null, loaded: string | null): boolean {
  if (!served || !loaded) return false;
  if (!served.startsWith('/assets/') || !loaded.startsWith('/assets/')) return false;
  return served !== loaded;
}

/** The script this page itself was loaded from, as a path. */
function loadedEntry(doc: Document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src]');
  if (!el) return null;
  try { return new URL(el.src, doc.baseURI).pathname; } catch { return null; }
}

/** True once the server is serving a build other than the one this page runs; it stays true until the reload. */
export function useNewBuild(): boolean {
  const [fresh, setFresh] = useState(false);
  useLiveRefresh(async (signal) => {
    if (fresh) return;
    const loaded = loadedEntry(document);
    if (!loaded?.startsWith('/assets/')) return;
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin', signal }).catch(() => null);
    if (!res || !res.ok) return;
    if (isNewBuild(entryScript(await res.text().catch(() => '')), loaded)) setFresh(true);
  }, LOOK_MS);
  return fresh;
}
